/**
 * The questions DANI asks before a Connect use case runs — pure, no React.
 *
 * One question per slot in the use case's template, each with recommended
 * options to pick from and a box under them, in the manner of Claude's own
 * clarifying questions. The answers fill the template (`prompt-template.ts`)
 * and the brand, competitors, markets and survey are remembered for the org
 * (`CompanyContext`), so they come back preselected and marked "Last used".
 *
 * The box under a question either takes a typed answer (`otherMode: 'answer'`)
 * or searches a list too long to show (`'search'`: the user's surveys, a
 * survey's questions), where only a picked option is ever an answer.
 *
 * An option whose value is `LET_DANI_CHOOSE` ('') prints the slot's fallback
 * wording ("the two nearest competitors", "my survey"), and is exclusive: it
 * clears the other picks, and picking anything else clears it.
 */

import type { ChannelWords } from './channel-words';
import { type CompanyContext, normalizeCompanyContext } from './company-context';
import { JOIN_WINDOW_OPTIONS, type JoinWords, RECOMMENDED_JOIN_WINDOW } from './join-survey';
import {
    fillTemplate,
    parseTemplate,
    type SlotKey,
    type SlotValues,
    templateSlots,
} from './prompt-template';

export const LET_DANI_CHOOSE = '';

export type OptionBadge = 'Recommended' | 'Last used' | 'Your company';

export interface QuestionOption {
    value: string;
    label: string;
    description?: string;
    badge?: OptionBadge;
}

export interface UseCaseQuestion {
    key: SlotKey;
    /** Short tab label. */
    header: string;
    question: string;
    multiSelect: boolean;
    options: QuestionOption[];
    /** What the box under the options does: take a typed answer, or search. */
    otherMode: 'answer' | 'search';
    otherPlaceholder: string;
    /** A line under the question saying what the answer is for, when that
     * is not obvious from the question DANI will be asked. */
    hint?: string;
}

/** Picked option values per question. Other text is added as a value too. */
export type Answers = Partial<Record<SlotKey, string[]>>;

/** A recommended option and why, e.g. a competitor, category or audience. */
export interface NamedSuggestion {
    name: string;
    reason: string;
}

/** What one recommendation call returns about a brand (`suggest-context.ts`). */
export interface ContextSuggestions {
    competitors: NamedSuggestion[];
    categories: NamedSuggestion[];
    audiences: NamedSuggestion[];
}

/** One of the user's surveys. `ref` is the `NNNNN-Name` reference DANI
 * resolves; `name` is what the user recognises. */
export interface SurveyChoice {
    ref: string;
    name: string;
    detail?: string;
}

/** One question from the picked survey's questionnaire. */
export interface SurveyQuestionChoice {
    qid: string;
    text: string;
}

export interface QuestionSuggestion {
    qid: string;
    reason: string;
}

/** Everything the questions are built from besides the template and the
 * remembered answers. Lists the card has not loaded yet are empty. */
export interface QuestionSources {
    orgName: string;
    /** The brand currently picked; words the competitor question. */
    brand: string;
    /** Recommended competitors, categories and audiences for the brand. */
    suggestions: ContextSuggestions;
    /** How the channel words its brand and competitor questions. */
    channelWords: ChannelWords;
    /** The use case's own wording for some of its questions. */
    ask?: Partial<Record<SlotKey, string>>;
    /** The surveys to offer: the user's newest, or what their search found. */
    surveys: readonly SurveyChoice[];
    /** The picked survey's questions to offer when the user is searching them. */
    surveyQuestionMatches: readonly SurveyQuestionChoice[];
    /** The picked survey's questions that best match the channel's data. */
    questionSuggestions: readonly (SurveyQuestionChoice & { reason: string })[];
    /** Whether the picked survey's questionnaire could be read (so its
     * question can be searched rather than typed). */
    hasQuestionnaire: boolean;
    /** The channel's words, for a Join my survey use case. */
    joinWords?: JoinWords;
}

/** The answer value for a survey question: what DANI is shown. */
export function surveyQuestionValue(question: SurveyQuestionChoice): string {
    return `${question.qid} "${question.text}"`;
}

export const MARKET_OPTIONS = {
    allUs: 'all US markets',
    top20: 'the top 20 DMAs',
} as const;

export const WINDOW_OPTIONS = [
    'the last 4 weeks',
    'the last 12 weeks',
    'the last 6 months',
    'the last 12 months',
] as const;

const RECOMMENDED_WINDOW = 'the last 12 weeks';

function sameName(a: string, b: string): boolean {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function brandQuestion(
    context: CompanyContext,
    orgName: string,
    words: ChannelWords,
): UseCaseQuestion {
    const options: QuestionOption[] = [];
    if (context.brand && !sameName(context.brand, orgName)) {
        options.push({ value: context.brand, label: context.brand, badge: 'Last used' });
    }
    if (orgName) {
        options.push({ value: orgName, label: orgName, badge: 'Your company' });
    }
    return {
        key: 'brand',
        header: 'Brand',
        question: `Which ${words.brandNoun} should DANI look at?`,
        multiSelect: false,
        options,
        otherMode: 'answer',
        otherPlaceholder: `Another ${words.brandNoun}`,
    };
}

function competitorQuestion(
    context: CompanyContext,
    brand: string,
    suggestions: readonly NamedSuggestion[],
    words: ChannelWords,
): UseCaseQuestion {
    const options: QuestionOption[] = context.competitors.map((name) => ({
        value: name,
        label: name,
        badge: 'Last used' as const,
    }));
    for (const [i, suggestion] of suggestions.entries()) {
        const existing = options.find((option) => sameName(option.value, suggestion.name));
        if (existing) {
            existing.description ??= suggestion.reason;
            continue;
        }
        options.push({
            value: suggestion.name,
            label: suggestion.name,
            description: suggestion.reason,
            badge: i === 0 ? 'Recommended' : undefined,
        });
    }
    options.push({
        value: LET_DANI_CHOOSE,
        label: 'Let DANI choose',
        description: words.competitorFallback ?? 'DANI picks the closest competitors',
    });
    return {
        key: 'competitors',
        header: 'Competitors',
        question: brand
            ? `Which competitors should DANI compare ${brand} against?`
            : 'Which competitors should DANI compare against?',
        multiSelect: true,
        options,
        otherMode: 'answer',
        otherPlaceholder: 'Another competitor',
    };
}

function marketQuestion(context: CompanyContext): UseCaseQuestion {
    const options: QuestionOption[] = [
        { value: MARKET_OPTIONS.allUs, label: 'All US markets', badge: 'Recommended' },
        { value: MARKET_OPTIONS.top20, label: 'The top 20 DMAs' },
    ];
    const known = Object.values(MARKET_OPTIONS) as string[];
    if (context.markets && !known.some((value) => sameName(value, context.markets))) {
        options.unshift({ value: context.markets, label: context.markets, badge: 'Last used' });
    }
    return {
        key: 'markets',
        header: 'Markets',
        question: 'Which markets should DANI cover?',
        multiSelect: false,
        options,
        otherMode: 'answer',
        otherPlaceholder: 'Dallas and Houston',
    };
}

function windowQuestion(): UseCaseQuestion {
    return {
        key: 'window',
        header: 'Time window',
        question: 'Over what period?',
        multiSelect: false,
        options: WINDOW_OPTIONS.map((value) => ({
            value,
            label: value.charAt(0).toUpperCase() + value.slice(1),
            badge: value === RECOMMENDED_WINDOW ? ('Recommended' as const) : undefined,
        })),
        otherMode: 'answer',
        otherPlaceholder: 'Since the campaign launched on May 1',
    };
}

function surveyQuestion(context: CompanyContext, sources: QuestionSources): UseCaseQuestion {
    const data = sources.joinWords?.data ?? 'this data';
    const options: QuestionOption[] = [];
    if (context.survey) {
        const known = sources.surveys.find((survey) => survey.ref === context.survey);
        options.push({
            value: context.survey,
            label: known?.name ?? context.survey,
            description: known?.detail,
            badge: 'Last used',
        });
    }
    for (const survey of sources.surveys) {
        if (survey.ref === context.survey) continue;
        options.push({ value: survey.ref, label: survey.name, description: survey.detail });
        if (options.length === MAX_LISTED) break;
    }
    options.push({
        value: LET_DANI_CHOOSE,
        label: 'Let DANI list my surveys',
        description: 'Pick one in the chat',
    });
    return {
        key: 'survey',
        header: 'Survey',
        question: `Which survey should DANI join with ${data}?`,
        multiSelect: false,
        options,
        otherMode: 'search',
        otherPlaceholder: 'Search all your surveys',
    };
}

function surveyQuestionQuestion(sources: QuestionSources): UseCaseQuestion {
    const data = sources.joinWords?.data ?? 'this data';
    const evidence = sources.joinWords?.evidence ?? 'what they actually did';
    const listed =
        sources.surveyQuestionMatches.length > 0
            ? sources.surveyQuestionMatches.map((question) => ({
                  value: surveyQuestionValue(question),
                  label: `${question.qid} · ${question.text}`,
              }))
            : sources.questionSuggestions.map((question, i) => ({
                  value: surveyQuestionValue(question),
                  label: `${question.qid} · ${question.text}`,
                  description: question.reason,
                  badge: i === 0 ? ('Recommended' as const) : undefined,
              }));
    return {
        key: 'question',
        header: 'Question',
        question: `Which answers should DANI check against ${evidence}?`,
        multiSelect: true,
        options: [
            ...listed.slice(0, MAX_LISTED),
            {
                value: LET_DANI_CHOOSE,
                label: 'Let DANI choose',
                description: `DANI picks the questions that best match ${data}`,
            },
        ],
        otherMode: sources.hasQuestionnaire ? 'search' : 'answer',
        otherPlaceholder: sources.hasQuestionnaire
            ? "Search this survey's questions"
            : 'A question number or wording, e.g. Q12',
    };
}

function joinWindowQuestion(sources: QuestionSources): UseCaseQuestion {
    const behavior = sources.joinWords?.behavior ?? 'behavior';
    return {
        key: 'joinWindow',
        header: 'Window',
        question: `Which ${behavior} should count?`,
        multiSelect: false,
        options: JOIN_WINDOW_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
            description: option.description,
            badge: option.value === RECOMMENDED_JOIN_WINDOW ? ('Recommended' as const) : undefined,
        })),
        otherMode: 'answer',
        otherPlaceholder: 'since the campaign launched on May 1',
    };
}

/** A question whose options are a remembered answer, then recommendations,
 * then "Let DANI …"; audiences and categories are both shaped this way. */
function suggestedQuestion(
    key: 'audience' | 'category',
    remembered: string,
    suggestions: readonly NamedSuggestion[],
    text: Omit<UseCaseQuestion, 'key' | 'options' | 'multiSelect' | 'otherMode'> & {
        letDani: QuestionOption;
    },
): UseCaseQuestion {
    const options: QuestionOption[] = [];
    if (remembered) options.push({ value: remembered, label: remembered, badge: 'Last used' });
    for (const [i, suggestion] of suggestions.entries()) {
        if (sameName(suggestion.name, remembered)) continue;
        options.push({
            value: suggestion.name,
            label: suggestion.name,
            description: suggestion.reason,
            badge: i === 0 && !remembered ? 'Recommended' : undefined,
        });
    }
    options.push(text.letDani);
    return {
        key,
        header: text.header,
        question: text.question,
        multiSelect: false,
        options,
        otherMode: 'answer',
        otherPlaceholder: text.otherPlaceholder,
    };
}

function audienceQuestion(context: CompanyContext, sources: QuestionSources): UseCaseQuestion {
    return suggestedQuestion('audience', context.audience, sources.suggestions.audiences, {
        header: 'Audience',
        question: 'Which audience should DANI look at?',
        otherPlaceholder: 'Parents who buy fast food weekly',
        letDani: {
            value: LET_DANI_CHOOSE,
            label: 'Let DANI define it',
            description: 'From your brand and category',
        },
    });
}

function categoryQuestion(context: CompanyContext, sources: QuestionSources): UseCaseQuestion {
    return suggestedQuestion('category', context.category, sources.suggestions.categories, {
        header: 'Category',
        question: 'Which category should DANI look at?',
        otherPlaceholder: 'Quick-service restaurants',
        letDani: {
            value: LET_DANI_CHOOSE,
            label: 'Let DANI work it out',
            description: 'From your brand',
        },
    });
}

/** How many surveys or survey questions a question lists at once. */
const MAX_LISTED = 5;

/**
 * The questions a use case asks, in order: one per slot in its template, plus
 * a brand question first when the use case recommends audiences or
 * categories but has no brand slot and no brand is remembered. That brand is
 * not part of the question DANI is asked; it is what the recommendations are
 * for, and it is remembered so later use cases do not ask again.
 */
export function questionSlots(template: string, context: CompanyContext): SlotKey[] {
    const slots = templateSlots(template);
    const recommends = slots.includes('audience') || slots.includes('category');
    return recommends && !slots.includes('brand') && !context.brand ? ['brand', ...slots] : slots;
}

/** Whether the brand is asked only to steer recommendations (see
 * `questionSlots`), not because the template names it. */
function brandSteersOnly(template: string, context: CompanyContext): boolean {
    return (
        !templateSlots(template).includes('brand') &&
        questionSlots(template, context).includes('brand')
    );
}

/** The questions for a template, in the order `questionSlots` gives. */
export function buildQuestions(
    template: string,
    context: CompanyContext,
    sources: QuestionSources,
): UseCaseQuestion[] {
    const build = (key: SlotKey): UseCaseQuestion => {
        switch (key) {
            case 'brand':
                return brandQuestion(context, sources.orgName, sources.channelWords);
            case 'competitors':
                return competitorQuestion(
                    context,
                    sources.brand,
                    sources.suggestions.competitors,
                    sources.channelWords,
                );
            case 'markets':
                return marketQuestion(context);
            case 'window':
                return windowQuestion();
            case 'survey':
                return surveyQuestion(context, sources);
            case 'question':
                return surveyQuestionQuestion(sources);
            case 'joinWindow':
                return joinWindowQuestion(sources);
            case 'audience':
                return audienceQuestion(context, sources);
            case 'category':
                return categoryQuestion(context, sources);
        }
    };
    return questionSlots(template, context).map((key) => {
        const question = build(key);
        if (key === 'brand' && brandSteersOnly(template, context)) {
            return {
                ...question,
                question: 'Which brand is this for?',
                hint: 'DANI uses it to recommend audiences and categories, and remembers it.',
            };
        }
        const own = sources.ask?.[key];
        return own ? { ...question, question: own } : question;
    });
}

/** What each question starts with picked: the remembered answer, or the
 * recommended one. Competitors start empty unless remembered, so a model's
 * guess is never sent without the user choosing it. */
export function startingAnswers(context: CompanyContext, orgName: string): Answers {
    return {
        brand: [context.brand || orgName].filter(Boolean),
        competitors: [...context.competitors],
        markets: [context.markets || MARKET_OPTIONS.allUs],
        window: [RECOMMENDED_WINDOW],
        survey: [context.survey || LET_DANI_CHOOSE],
        question: [LET_DANI_CHOOSE],
        joinWindow: [RECOMMENDED_JOIN_WINDOW],
        audience: [context.audience || LET_DANI_CHOOSE],
        category: [context.category || LET_DANI_CHOOSE],
    };
}

/** Picks or unpicks one value, keeping `LET_DANI_CHOOSE` exclusive. */
export function toggleAnswer(
    current: readonly string[],
    value: string,
    multiSelect: boolean,
): string[] {
    if (!multiSelect) return [value];
    if (current.includes(value)) return current.filter((item) => item !== value);
    if (value === LET_DANI_CHOOSE) return [LET_DANI_CHOOSE];
    return [...current.filter((item) => item !== LET_DANI_CHOOSE), value];
}

/** The answered brand, or '' when none. */
export function answeredBrand(answers: Answers): string {
    return (answers.brand?.[0] ?? '').trim();
}

function values(answers: Answers, key: SlotKey): string[] {
    return (answers[key] ?? []).map((value) => value.trim()).filter(Boolean);
}

function slotValues(answers: Answers): SlotValues {
    return {
        brand: answeredBrand(answers),
        competitors: values(answers, 'competitors'),
        markets: values(answers, 'markets')[0] ?? '',
        window: values(answers, 'window')[0] ?? '',
        // A picked survey is its reference; DANI is told it is the user's own.
        survey: values(answers, 'survey')[0] ? `my survey ${values(answers, 'survey')[0]}` : '',
        question: values(answers, 'question'),
        joinWindow: values(answers, 'joinWindow')[0] ?? '',
        audience: values(answers, 'audience')[0] ?? '',
        category: values(answers, 'category')[0] ?? '',
    };
}

/** The question DANI is asked. */
export function promptFromAnswers(template: string, answers: Answers): string {
    return fillTemplate(template, slotValues(answers));
}

/** One piece of the question as the card previews it: plain text, or a
 * slot showing its answer (`answered`) or its fallback wording. */
export interface PreviewPart {
    text: string;
    slot?: SlotKey;
    answered?: boolean;
}

/** The question DANI will ask, in parts, so the card can highlight what each
 * answer changed. Joined, the parts read as `promptFromAnswers`. */
export function previewParts(template: string, answers: Answers): PreviewPart[] {
    const filled = slotValues(answers);
    return parseTemplate(template).map((segment) => {
        if (segment.type === 'text') return { text: segment.text };
        const value = fillTemplate(`{${segment.key}}`, filled);
        return value
            ? { text: value, slot: segment.key, answered: true }
            : { text: segment.fallback, slot: segment.key, answered: false };
    });
}

/** What to remember for the org. Only questions this use case asked change
 * anything, and "Let DANI choose" keeps what was remembered. */
export function contextFromAnswers(
    previous: CompanyContext,
    template: string,
    answers: Answers,
): CompanyContext {
    const asked = questionSlots(template, previous);
    const competitors = values(answers, 'competitors');
    // A one-answer question this use case asked, or what was remembered.
    const single = (key: 'brand' | 'markets' | 'survey' | 'audience' | 'category') =>
        (asked.includes(key) && values(answers, key)[0]) || previous[key];
    return normalizeCompanyContext({
        brand: single('brand'),
        competitors:
            asked.includes('competitors') && competitors.length > 0
                ? competitors
                : previous.competitors,
        markets: single('markets'),
        survey: single('survey'),
        audience: single('audience'),
        category: single('category'),
    });
}
